import React from 'react';

interface TransactionStatusModalProps {
  isOpen: boolean;
  onClose: () => void;
  statusData: any;
}

const TransactionStatusModal: React.FC<TransactionStatusModalProps> = ({
  isOpen,
  onClose,
  statusData
}) => {
  if (!isOpen || !statusData || !statusData.data) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const data = statusData.data;
  
  // Parse transaction date (DD-MM-YYYY format)
  const parseTransactionDate = (dateString: string): Date | null => {
    if (!dateString) return null;
    const ddMMyyyyPattern = /^(\d{2})-(\d{2})-(\d{4})$/;
    const match = dateString.match(ddMMyyyyPattern);
    if (match) {
      const [, day, month, year] = match;
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      if (date.getFullYear() === parseInt(year) && 
          date.getMonth() === parseInt(month) - 1 && 
          date.getDate() === parseInt(day)) {
        return date;
      }
    }
    const parsed = new Date(dateString);
    return !isNaN(parsed.getTime()) ? parsed : null;
  };

  const formatDate = (dateString: string | Date | null, includeTime: boolean = true): string => {
    if (!dateString) return 'N/A';
    let date: Date | null = null;
    let isDateOnly = false;
    
    if (dateString instanceof Date) {
      date = dateString;
    } else if (typeof dateString === 'string') {
      const ddMMyyyyPattern = /^(\d{2})-(\d{2})-(\d{4})$/;
      if (ddMMyyyyPattern.test(dateString)) {
        isDateOnly = true;
      }
      date = parseTransactionDate(dateString);
    }
    
    if (!date || isNaN(date.getTime())) {
      return 'Invalid Date';
    }
    
    if (isDateOnly || !includeTime) {
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    }
    
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getStatusColorClass = (status: string) => {
    const statusUpper = status?.toUpperCase() || '';
    switch (statusUpper) {
      case 'SUCCESS':
        return 'bg-emerald-500 text-white';
      case 'FAILED':
        return 'bg-red-500 text-white';
      case 'PENDING':
      case 'PROCESSING':
        return 'bg-amber-500 text-white';
      default:
        return 'bg-slate-500 text-white';
    }
  };
  const getStatusTextClass = (status: string) => {
    const statusUpper = status?.toUpperCase() || '';
    switch (statusUpper) {
      case 'SUCCESS': return 'text-emerald-600';
      case 'FAILED': return 'text-red-600';
      case 'PENDING':
      case 'PROCESSING': return 'text-amber-600';
      default: return 'text-slate-600';
    }
  };

  const getStatusIcon = (status: string) => {
    const statusUpper = status?.toUpperCase() || '';
    switch (statusUpper) {
      case 'SUCCESS':
        return '✓';
      case 'FAILED':
        return '✕';
      case 'PENDING':
      case 'PROCESSING':
        return '⏳';
      default:
        return 'ℹ';
    }
  };

  const status = data.status || 'UNKNOWN';
  const statusColorClass = getStatusColorClass(status);
  const statusTextClass = getStatusTextClass(status);
  const transactionDate = data.transaction_date || 
                         data.decrypted_response?.transaction_date ||
                         data.decrypted_response?.response_body?.transaction_date;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[10000] p-4 backdrop-blur-sm" onClick={handleBackdropClick}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center text-xl font-bold ${statusColorClass}`}>
              {getStatusIcon(status)}
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-800 m-0">Transaction Status</h2>
              <span className={`text-sm font-semibold ${statusTextClass}`}>{status}</span>
            </div>
          </div>
          <button type="button" className="w-10 h-10 flex items-center justify-center rounded-lg border-none text-slate-500 hover:bg-slate-100 cursor-pointer" onClick={onClose} aria-label="Close">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
          </button>
        </div>

        <div className="p-5 overflow-y-auto flex-1">
          <div className="grid grid-cols-1 gap-3">
            {data.merchant_reference_number && (
              <div className="flex gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100">
                <span className="text-xl">📋</span>
                <div className="min-w-0 flex-1">
                  <span className="block text-xs font-semibold text-slate-500 uppercase">Merchant Reference</span>
                  <span className="block text-sm font-mono text-slate-800 truncate">{data.merchant_reference_number}</span>
                </div>
              </div>
            )}
            {data.viyona_transaction_id && (
              <div className="flex gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100">
                <span className="text-xl">🔢</span>
                <div className="min-w-0 flex-1">
                  <span className="block text-xs font-semibold text-slate-500 uppercase">Transaction ID</span>
                  <span className="block text-sm font-mono text-slate-800 truncate">{data.viyona_transaction_id}</span>
                </div>
              </div>
            )}
            {data.amount && (
              <div className="flex gap-3 p-3 rounded-xl bg-violet-50 border border-violet-100">
                <span className="text-xl">💰</span>
                <div className="min-w-0 flex-1">
                  <span className="block text-xs font-semibold text-slate-500 uppercase">Amount</span>
                  <span className="block text-base font-bold text-slate-800">
                    ₹{new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(parseFloat(data.amount))}
                  </span>
                </div>
              </div>
            )}
            {data.bank_reference_number && (
              <div className="flex gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100">
                <span className="text-xl">🏦</span>
                <div className="min-w-0 flex-1">
                  <span className="block text-xs font-semibold text-slate-500 uppercase">Bank Reference</span>
                  <span className="block text-sm font-mono text-slate-800 truncate">{data.bank_reference_number}</span>
                </div>
              </div>
            )}
            {transactionDate && (
              <div className="flex gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100">
                <span className="text-xl">📅</span>
                <div className="min-w-0 flex-1">
                  <span className="block text-xs font-semibold text-slate-500 uppercase">Transaction Date</span>
                  <span className="block text-sm text-slate-800">{formatDate(transactionDate, false)}</span>
                </div>
              </div>
            )}
            {data.created_at && (
              <div className="flex gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100">
                <span className="text-xl">🕐</span>
                <div className="min-w-0 flex-1">
                  <span className="block text-xs font-semibold text-slate-500 uppercase">Transaction Time</span>
                  <span className="block text-sm text-slate-800">{formatDate(data.created_at)}</span>
                </div>
              </div>
            )}
            {data.updated_at && (
              <div className="flex gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100">
                <span className="text-xl">🔄</span>
                <div className="min-w-0 flex-1">
                  <span className="block text-xs font-semibold text-slate-500 uppercase">Last Updated</span>
                  <span className="block text-sm text-slate-800">{formatDate(data.updated_at)}</span>
                </div>
              </div>
            )}
          </div>

          {data.error_message && (
            <div className="mt-4 flex gap-3 p-4 rounded-xl border-2 border-amber-200 bg-amber-50">
              <span className="text-xl flex-shrink-0">⚠️</span>
              <p className="text-sm text-amber-800 m-0">{data.error_message}</p>
            </div>
          )}

          {data.decrypted_response && (
            <details className="mt-4 rounded-xl border border-slate-200 overflow-hidden">
              <summary className="p-3 bg-slate-100 cursor-pointer text-sm font-semibold text-slate-700">View Decrypted Response Data</summary>
              <pre className="p-4 bg-slate-900 text-slate-100 text-xs overflow-x-auto m-0 font-mono">
                {JSON.stringify(data.decrypted_response, null, 2)}
              </pre>
            </details>
          )}
        </div>

        <div className="p-5 border-t border-slate-200">
          <button type="button" onClick={onClose} className="w-full py-3 px-5 rounded-xl bg-violet-600 text-white font-semibold text-sm flex items-center justify-center gap-2 cursor-pointer hover:bg-violet-700">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
            Got it!
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransactionStatusModal;
